import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';

const BUCKET = 'gallery';

const toPublicUrl = (path) => {
  if (!supabase || !path) return null;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return data?.publicUrl || null;
};

const normalizeRow = (row) => {
  const imagePaths = Array.isArray(row.image_paths) ? row.image_paths : [];
  const images = imagePaths.map((p) => toPublicUrl(p)).filter(Boolean);
  return {
    id: row.id,
    title: row.title || 'Gallery',
    category: row.category || '',
    description: row.description || '',
    images,
    image_paths: imagePaths
  };
};

export const galleryStoreEnabled = () => isSupabaseConfigured && Boolean(supabase);

export async function fetchGalleryAlbums() {
  if (!galleryStoreEnabled()) return [];
  const { data, error } = await supabase
    .from('gallery_albums')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeRow);
}

export async function createGalleryAlbum(input) {
  if (!galleryStoreEnabled()) throw new Error('Supabase not configured');
  const id = input.id || crypto.randomUUID();
  const row = {
    id,
    title: input.title || 'Gallery',
    category: input.category || '',
    description: input.description || '',
    image_paths: Array.isArray(input.image_paths) ? input.image_paths : []
  };
  const { data, error } = await supabase.from('gallery_albums').insert(row).select('*').single();
  if (error) throw error;
  return normalizeRow(data);
}

export async function updateGalleryAlbum(id, patch) {
  if (!galleryStoreEnabled()) throw new Error('Supabase not configured');
  const rowPatch = {};
  if (patch.title !== undefined) rowPatch.title = patch.title;
  if (patch.category !== undefined) rowPatch.category = patch.category;
  if (patch.description !== undefined) rowPatch.description = patch.description;
  if (patch.image_paths !== undefined) rowPatch.image_paths = patch.image_paths;

  const { data, error } = await supabase.from('gallery_albums').update(rowPatch).eq('id', id).select('*').single();
  if (error) throw error;
  return normalizeRow(data);
}

export async function deleteGalleryAlbum(id, imagePaths = []) {
  if (!galleryStoreEnabled()) throw new Error('Supabase not configured');
  await supabase.from('gallery_albums').delete().eq('id', id);
  if (Array.isArray(imagePaths) && imagePaths.length) {
    await supabase.storage.from(BUCKET).remove(imagePaths);
  }
}

export async function uploadGalleryImages(albumId, files) {
  if (!galleryStoreEnabled()) throw new Error('Supabase not configured');
  const list = Array.from(files || []);
  if (!list.length) return [];

  const uploadedPaths = [];
  for (const file of list) {
    const safeName = String(file.name || 'image').replaceAll(' ', '-');
    const path = `albums/${albumId}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
    if (error) throw error;
    uploadedPaths.push(path);
  }
  return uploadedPaths;
}

